import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Stack,
  Text,
  Pivot,
  PivotItem,
  mergeStyleSets,
  getTheme,
  IStackTokens,
  DefaultButton,
  PrimaryButton,
  MessageBar,
  MessageBarType,
  Icon,
  SearchBox,
  Spinner,
  SpinnerSize,
  Dropdown,
  IDropdownOption,
  Toggle,
  TooltipHost,
  DirectionalHint
} from '@fluentui/react';
import { buildApiUrl } from '../config/apiConfig';

const theme = getTheme();
const stackTokens: IStackTokens = { childrenGap: 16 };

const styles = mergeStyleSets({
  container: {
    maxWidth: 1600,
    margin: '0 auto',
    padding: '20px'
  },
  header: {
    padding: '20px',
    backgroundColor: theme.palette.themePrimary,
    color: 'white',
    borderRadius: '8px',
    marginBottom: '20px'
  },
  statsBar: {
    display: 'flex',
    gap: '24px',
    padding: '16px',
    backgroundColor: theme.palette.neutralLighterAlt,
    borderRadius: '8px',
    marginBottom: '20px'
  },
  statItem: {
    textAlign: 'center' as const
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: theme.palette.themePrimary
  },
  statLabel: {
    fontSize: '12px',
    color: theme.palette.neutralSecondary
  },
  filterBar: {
    display: 'flex',
    gap: '16px',
    padding: '16px',
    backgroundColor: theme.palette.white,
    borderRadius: '8px',
    border: `1px solid ${theme.palette.neutralLight}`,
    marginBottom: '20px',
    flexWrap: 'wrap' as const,
    alignItems: 'center'
  },
  categorySection: {
    marginBottom: '24px'
  },
  categoryHeader: {
    padding: '12px 16px',
    backgroundColor: theme.palette.neutralLighterAlt,
    borderRadius: '8px 8px 0 0',
    borderBottom: `2px solid ${theme.palette.themePrimary}`,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  categoryContent: {
    border: `1px solid ${theme.palette.neutralLight}`,
    borderTop: 'none',
    borderRadius: '0 0 8px 8px',
    padding: '16px'
  },
  endpointCard: {
    padding: '12px 16px',
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: '6px',
    backgroundColor: theme.palette.white,
    marginBottom: '12px',
    transition: 'box-shadow 0.2s ease',
    ':hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }
  },
  methodBadge: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
    color: 'white',
    display: 'inline-block',
    minWidth: '55px',
    textAlign: 'center' as const,
    marginRight: '12px'
  },
  getBadge: {
    backgroundColor: '#61affe'
  },
  postBadge: {
    backgroundColor: '#49cc90'
  },
  putBadge: {
    backgroundColor: '#fca130'
  },
  deleteBadge: {
    backgroundColor: '#f93e3e'
  },
  patchBadge: {
    backgroundColor: '#50e3c2'
  },
  path: {
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
    fontSize: '13px',
    color: theme.palette.neutralPrimary,
    flex: 1
  },
  summary: {
    fontSize: '13px',
    color: theme.palette.neutralSecondary,
    marginLeft: '16px',
    flex: 2
  },
  authBadge: {
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '10px',
    fontWeight: 600,
    marginLeft: '8px'
  },
  authRequired: {
    backgroundColor: '#fff3cd',
    color: '#856404'
  },
  authPublic: {
    backgroundColor: '#d4edda',
    color: '#155724'
  },
  deprecatedBadge: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '10px',
    fontWeight: 600,
    marginLeft: '8px'
  },
  tagBadge: {
    padding: '2px 6px',
    borderRadius: '10px',
    fontSize: '10px',
    backgroundColor: theme.palette.neutralLighter,
    color: theme.palette.neutralPrimary,
    marginLeft: '4px'
  },
  codeBlock: {
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
    padding: '16px',
    borderRadius: '6px',
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
    fontSize: '12px',
    overflowX: 'auto' as const,
    marginTop: '12px'
  },
  securitySection: {
    padding: '20px',
    backgroundColor: theme.palette.neutralLighterAlt,
    borderRadius: '8px',
    marginTop: '20px'
  },
  securityItem: {
    padding: '12px',
    backgroundColor: theme.palette.white,
    borderRadius: '6px',
    marginBottom: '8px',
    border: `1px solid ${theme.palette.neutralLight}`
  }
});

interface OpenAPISpec {
  openapi: string;
  info: {
    title: string;
    description: string;
    version: string;
    contact?: { name?: string; email?: string };
  };
  paths: Record<string, Record<string, PathOperation>>;
  tags?: Array<{ name: string; description?: string }>;
  components?: {
    securitySchemes?: Record<string, SecurityScheme>;
  };
}

interface PathOperation {
  summary: string;
  description?: string;
  tags?: string[];
  deprecated?: boolean;
  security?: Array<Record<string, string[]>>;
  parameters?: Array<{
    name: string;
    in: string;
    required?: boolean;
    schema?: { type?: string };
  }>;
  responses?: Record<string, { description?: string }>;
}

interface SecurityScheme {
  type: string;
  scheme?: string;
  bearerFormat?: string;
  description?: string;
  flows?: Record<string, { scopes?: Record<string, string> }>;
}

interface RouteInfo {
  method: string;
  path: string;
  summary: string;
  description: string;
  tags: string[];
  deprecated: boolean;
  authRequired: boolean;
  parameters: Array<{ name: string; in: string; required: boolean; type: string }>;
}

export default function SwaggerDocsPage() {
  const [spec, setSpec] = useState<OpenAPISpec | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMethod, setSelectedMethod] = useState<string>('all');
  const [selectedTag, setSelectedTag] = useState<string>('all');
  const [showAuthOnly, setShowAuthOnly] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [selectedPivotKey, setSelectedPivotKey] = useState<string>('routes');

  // Fetch OpenAPI spec
  useEffect(() => {
    const fetchSpec = async () => {
      try {
        setLoading(true);
        const response = await fetch(buildApiUrl('/openapi.json'), { credentials: 'include' });
        if (!response.ok) {
          throw new Error(`Failed to fetch API spec: ${response.status}`);
        }
        const data = await response.json();
        setSpec(data);
        // Expand first 3 categories by default
        const tags = Array.from(new Set(
          Object.values(data.paths || {}).flatMap((methods: any) =>
            Object.values(methods).flatMap((op: any) => op.tags || [])
          )
        )).slice(0, 3);
        setExpandedCategories(new Set(tags));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load API documentation');
      } finally {
        setLoading(false);
      }
    };
    fetchSpec();
  }, []);

  // Parse routes from spec
  const routes: RouteInfo[] = useMemo(() => {
    if (!spec?.paths) return [];
    
    const result: RouteInfo[] = [];
    for (const [path, methods] of Object.entries(spec.paths)) {
      for (const [method, operation] of Object.entries(methods)) {
        if (typeof operation !== 'object') continue;
        
        const op = operation as PathOperation;
        result.push({
          method: method.toUpperCase(),
          path,
          summary: op.summary || '',
          description: op.description || op.summary || '',
          tags: op.tags || ['Other'],
          deprecated: op.deprecated || false,
          authRequired: (op.security?.length ?? 0) > 0,
          parameters: (op.parameters || []).map(p => ({
            name: p.name,
            in: p.in,
            required: p.required || false,
            type: p.schema?.type || 'string'
          }))
        });
      }
    }
    return result.sort((a, b) => a.path.localeCompare(b.path));
  }, [spec]);

  // Get unique tags
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    routes.forEach(r => r.tags.forEach(t => tags.add(t)));
    return Array.from(tags).sort();
  }, [routes]);

  // Filter routes
  const filteredRoutes = useMemo(() => {
    return routes.filter(route => {
      if (searchQuery && !route.path.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !route.summary.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (selectedMethod !== 'all' && route.method !== selectedMethod) {
        return false;
      }
      if (selectedTag !== 'all' && !route.tags.includes(selectedTag)) {
        return false;
      }
      if (showAuthOnly && !route.authRequired) {
        return false;
      }
      return true;
    });
  }, [routes, searchQuery, selectedMethod, selectedTag, showAuthOnly]);

  // Group routes by primary tag
  const groupedRoutes = useMemo(() => {
    const groups: Record<string, RouteInfo[]> = {};
    filteredRoutes.forEach(route => {
      const primaryTag = route.tags[0] || 'Other';
      if (!groups[primaryTag]) groups[primaryTag] = [];
      groups[primaryTag].push(route);
    });
    return groups;
  }, [filteredRoutes]);

  // Stats
  const stats = useMemo(() => ({
    total: routes.length,
    filtered: filteredRoutes.length,
    get: routes.filter(r => r.method === 'GET').length,
    post: routes.filter(r => r.method === 'POST').length,
    put: routes.filter(r => r.method === 'PUT').length,
    delete: routes.filter(r => r.method === 'DELETE').length,
    authenticated: routes.filter(r => r.authRequired).length,
    public: routes.filter(r => !r.authRequired).length,
    categories: allTags.length
  }), [routes, filteredRoutes, allTags]);

  const toggleCategory = useCallback((tag: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedCategories(new Set(allTags));
  }, [allTags]);

  const collapseAll = useCallback(() => {
    setExpandedCategories(new Set());
  }, []);

  const getMethodBadgeClass = (method: string) => {
    switch (method) {
      case 'GET': return `${styles.methodBadge} ${styles.getBadge}`;
      case 'POST': return `${styles.methodBadge} ${styles.postBadge}`;
      case 'PUT': return `${styles.methodBadge} ${styles.putBadge}`;
      case 'DELETE': return `${styles.methodBadge} ${styles.deleteBadge}`;
      case 'PATCH': return `${styles.methodBadge} ${styles.patchBadge}`;
      default: return styles.methodBadge;
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const methodOptions: IDropdownOption[] = [
    { key: 'all', text: 'All Methods' },
    { key: 'GET', text: 'GET' },
    { key: 'POST', text: 'POST' },
    { key: 'PUT', text: 'PUT' },
    { key: 'DELETE', text: 'DELETE' },
  ];

  const tagOptions: IDropdownOption[] = [
    { key: 'all', text: 'All Categories' },
    ...allTags.map(tag => ({ key: tag, text: tag }))
  ];

  if (loading) {
    return (
      <div className={styles.container}>
        <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { minHeight: 400 } }}>
          <Spinner size={SpinnerSize.large} label="Loading API Documentation..." />
        </Stack>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <MessageBar messageBarType={MessageBarType.error}>
          {error}
        </MessageBar>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 16 }}>
          <Icon iconName="Code" styles={{ root: { fontSize: 32 } }} />
          <Stack>
            <Text variant="xxLarge" styles={{ root: { color: 'white', fontWeight: 700 } }}>
              {spec?.info?.title || 'API Documentation'}
            </Text>
            <Text variant="medium" styles={{ root: { color: 'rgba(255,255,255,0.9)' } }}>
              Version {spec?.info?.version || '1.0.0'} • OpenAPI 3.0
            </Text>
          </Stack>
        </Stack>
      </div>

      {/* Stats Bar */}
      <div className={styles.statsBar}>
        <div className={styles.statItem}>
          <div className={styles.statValue}>{stats.total}</div>
          <div className={styles.statLabel}>Total Endpoints</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statValue}>{stats.categories}</div>
          <div className={styles.statLabel}>Categories</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statValue} style={{ color: '#61affe' }}>{stats.get}</div>
          <div className={styles.statLabel}>GET</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statValue} style={{ color: '#49cc90' }}>{stats.post}</div>
          <div className={styles.statLabel}>POST</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statValue} style={{ color: '#fca130' }}>{stats.put}</div>
          <div className={styles.statLabel}>PUT</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statValue} style={{ color: '#f93e3e' }}>{stats.delete}</div>
          <div className={styles.statLabel}>DELETE</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statValue} style={{ color: '#856404' }}>{stats.authenticated}</div>
          <div className={styles.statLabel}>Authenticated</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statValue} style={{ color: '#155724' }}>{stats.public}</div>
          <div className={styles.statLabel}>Public</div>
        </div>
      </div>

      <Pivot
        selectedKey={selectedPivotKey}
        onLinkClick={(item) => setSelectedPivotKey(item?.props.itemKey || 'routes')}
      >
        <PivotItem headerText="API Routes" itemIcon="Code" itemKey="routes">
          {/* Filter Bar */}
          <div className={styles.filterBar}>
            <SearchBox
              placeholder="Search endpoints..."
              styles={{ root: { minWidth: 250 } }}
              value={searchQuery}
              onChange={(_, value) => setSearchQuery(value || '')}
            />
            <Dropdown
              placeholder="Method"
              options={methodOptions}
              selectedKey={selectedMethod}
              onChange={(_, option) => setSelectedMethod(option?.key as string || 'all')}
              styles={{ root: { minWidth: 120 } }}
            />
            <Dropdown
              placeholder="Category"
              options={tagOptions}
              selectedKey={selectedTag}
              onChange={(_, option) => setSelectedTag(option?.key as string || 'all')}
              styles={{ root: { minWidth: 180 } }}
            />
            <Toggle
              label="Auth Required Only"
              inlineLabel
              checked={showAuthOnly}
              onChange={(_, checked) => setShowAuthOnly(checked || false)}
            />
            <div style={{ flex: 1 }} />
            <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
              Showing {stats.filtered} of {stats.total} endpoints
            </Text>
            <DefaultButton iconProps={{ iconName: 'ChevronDown' }} onClick={expandAll}>
              Expand All
            </DefaultButton>
            <DefaultButton iconProps={{ iconName: 'ChevronUp' }} onClick={collapseAll}>
              Collapse All
            </DefaultButton>
          </div>

          {/* Routes by Category */}
          <Stack tokens={{ childrenGap: 16 }}>
            {Object.entries(groupedRoutes).sort(([a], [b]) => a.localeCompare(b)).map(([tag, tagRoutes]) => (
              <div key={tag} className={styles.categorySection}>
                <div 
                  className={styles.categoryHeader}
                  onClick={() => toggleCategory(tag)}
                >
                  <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <Icon iconName={expandedCategories.has(tag) ? 'ChevronDown' : 'ChevronRight'} />
                    <Text variant="large" styles={{ root: { fontWeight: 600 } }}>{tag}</Text>
                    <Text variant="small" styles={{ root: { 
                      color: theme.palette.neutralSecondary,
                      backgroundColor: theme.palette.neutralLighter,
                      padding: '2px 8px',
                      borderRadius: '12px'
                    } }}>
                      {tagRoutes.length} endpoints
                    </Text>
                  </Stack>
                </div>
                
                {expandedCategories.has(tag) && (
                  <div className={styles.categoryContent}>
                    {tagRoutes.map((route, idx) => (
                      <div key={`${route.method}-${route.path}-${idx}`} className={styles.endpointCard}>
                        <Stack horizontal verticalAlign="center">
                          <span className={getMethodBadgeClass(route.method)}>
                            {route.method}
                          </span>
                          <TooltipHost content="Click to copy" directionalHint={DirectionalHint.topCenter}>
                            <span 
                              className={styles.path}
                              style={{ cursor: 'pointer' }}
                              onClick={() => copyToClipboard(route.path)}
                            >
                              {route.path}
                            </span>
                          </TooltipHost>
                          <span className={styles.summary}>{route.summary}</span>
                          {route.authRequired ? (
                            <span className={`${styles.authBadge} ${styles.authRequired}`}>🔒 Auth</span>
                          ) : (
                            <span className={`${styles.authBadge} ${styles.authPublic}`}>🌐 Public</span>
                          )}
                          {route.deprecated && (
                            <span className={styles.deprecatedBadge}>⚠️ Deprecated</span>
                          )}
                          {route.tags.slice(1).map(t => (
                            <span key={t} className={styles.tagBadge}>{t}</span>
                          ))}
                        </Stack>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </Stack>
        </PivotItem>

        <PivotItem headerText="Overview" itemIcon="Info" itemKey="overview">
          <Stack tokens={stackTokens}>
            <MessageBar messageBarType={MessageBarType.info}>
              This documentation is auto-generated from the OpenAPI specification at <code>/openapi.json</code>
            </MessageBar>
            
            <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>About the API</Text>
            <div 
              dangerouslySetInnerHTML={{ 
                __html: (spec?.info?.description || '').replace(/\n/g, '<br/>').replace(/```json([\s\S]*?)```/g, '<pre>$1</pre>') 
              }} 
            />

            <Text variant="xLarge" styles={{ root: { fontWeight: 600, marginTop: 24 } }}>Base URLs</Text>
            <div className={styles.codeBlock}>
              <div>Production: <strong>https://your-domain.azurewebsites.net</strong></div>
              <div>Local Development: <strong>http://localhost:5050</strong></div>
            </div>

            <Text variant="xLarge" styles={{ root: { fontWeight: 600, marginTop: 24 } }}>Response Format</Text>
            <Text variant="medium">All API responses follow a consistent JSON format:</Text>
            <div className={styles.codeBlock}>
{`// Success Response
{
  "success": true,
  "data": { ... },
  "message": "Operation completed successfully"
}

// Error Response
{
  "success": false,
  "error": "Error description",
  "code": "ERROR_CODE"
}`}
            </div>
          </Stack>
        </PivotItem>

        <PivotItem headerText="Security" itemIcon="Shield" itemKey="security">
          <Stack tokens={stackTokens}>
            <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>Authentication Methods</Text>
            
            <div className={styles.securitySection}>
              {spec?.components?.securitySchemes && Object.entries(spec.components.securitySchemes).map(([name, scheme]) => (
                <div key={name} className={styles.securityItem}>
                  <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                    <Icon iconName="Lock" styles={{ root: { fontSize: 20, color: theme.palette.themePrimary } }} />
                    <Stack>
                      <Text variant="large" styles={{ root: { fontWeight: 600 } }}>{name}</Text>
                      <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                        {scheme.description || `Type: ${scheme.type}`}
                      </Text>
                    </Stack>
                  </Stack>
                  {scheme.type === 'http' && (
                    <div className={styles.codeBlock}>
                      Authorization: Bearer {'<your-jwt-token>'}
                    </div>
                  )}
                  {scheme.type === 'apiKey' && (
                    <div className={styles.codeBlock}>
                      Cookie: session={'<session-id>'}
                    </div>
                  )}
                  {scheme.flows?.authorizationCode?.scopes && (
                    <Stack tokens={{ childrenGap: 4 }} styles={{ root: { marginTop: 12 } }}>
                      <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>OAuth Scopes:</Text>
                      {Object.entries(scheme.flows.authorizationCode.scopes).map(([scope, desc]) => (
                        <Text key={scope} variant="small">
                          • <code>{scope}</code>: {desc}
                        </Text>
                      ))}
                    </Stack>
                  )}
                </div>
              ))}
            </div>

            <Text variant="xLarge" styles={{ root: { fontWeight: 600, marginTop: 24 } }}>Security Recommendations</Text>
            <MessageBar messageBarType={MessageBarType.warning}>
              <Stack tokens={{ childrenGap: 8 }}>
                <Text>• Always use HTTPS in production</Text>
                <Text>• Rotate API keys and tokens regularly</Text>
                <Text>• Implement rate limiting for public endpoints</Text>
                <Text>• Use role-based access control (RBAC) for admin routes</Text>
                <Text>• Enable audit logging for sensitive operations</Text>
              </Stack>
            </MessageBar>
          </Stack>
        </PivotItem>

        <PivotItem headerText="Download" itemIcon="Download" itemKey="download">
          <Stack tokens={stackTokens}>
            <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>Export API Specification</Text>
            
            <Stack horizontal tokens={{ childrenGap: 16 }}>
              <PrimaryButton
                iconProps={{ iconName: 'Download' }}
                onClick={() => {
                  const blob = new Blob([JSON.stringify(spec, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'openapi-spec.json';
                  a.click();
                }}
              >
                Download OpenAPI JSON
              </PrimaryButton>
              
              <DefaultButton
                iconProps={{ iconName: 'Copy' }}
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(spec, null, 2));
                }}
              >
                Copy to Clipboard
              </DefaultButton>
            </Stack>

            <Text variant="medium" styles={{ root: { marginTop: 16 } }}>
              Use this specification with tools like:
            </Text>
            <ul>
              <li><strong>Swagger UI</strong> - Interactive API documentation</li>
              <li><strong>Postman</strong> - API testing and collections</li>
              <li><strong>OpenAPI Generator</strong> - Generate client SDKs</li>
              <li><strong>Insomnia</strong> - REST API client</li>
            </ul>

            <Text variant="xLarge" styles={{ root: { fontWeight: 600, marginTop: 24 } }}>API Endpoints</Text>
            <div className={styles.codeBlock}>
              /openapi.json - OpenAPI 3.0 Specification{'\n'}
              /docs - Redirect to API documentation{'\n'}
              /api-docs - Redirect to API documentation
            </div>
          </Stack>
        </PivotItem>
      </Pivot>
    </div>
  );
}
